// Color scheme per registered action — same per-actionKey config convention
// as actionConditions.js/actionPayloadSchemas.js in this folder, and the
// same scheme vocabulary as src/lib/statusStyles.js's STATUS_SCHEMES
// (emerald/amber/red/teal/indigo/violet/blue/orange/slate/gray), so an
// action's color and a status chip's color always draw from one shared
// palette instead of two drifting ones. Grouped by what each action DOES,
// not how it looks — add a newly-registered action key to whichever group
// matches its intent. actions/index.js's own registrations are the full
// source of truth for which keys actually exist; this file only decides how
// to color whichever of them show up here.
//
// `view_scores` (default flow) is deliberately NOT mapped — it's a
// persistent icon-only nav button (ghost/icon variant), not a labeled
// action in a button/kebab group, so it falls through to the `gray`
// fallback (i.e. stays visually unchanged) rather than being forced into a
// tone that doesn't fit its pattern.

// Destructive / negative outcome for the candidate.
const RED_ACTIONS = [
  'remove_from_pipeline',
  'mark_not_interested',
  'mark_as_non_responsive',
  'reject',
  'mark_as_no_show',
];

// Positive / genuinely moves the candidate forward in the pipeline — not
// just "acknowledge" (see TEAL_ACTIONS below for why that's a separate,
// lighter-weight group despite also reading as "positive").
const EMERALD_ACTIONS = [
  'complete_interview_round',
  'move_to_shortlisted',
  'forward_to_cse',
];

// Communication / outreach to the candidate.
const BLUE_ACTIONS = [
  'send_contact_link',
  'resend_contact_link',
  'resend_test_link',
  'resend_assessment_link',
  'assign_test',
];

// Confirmation / receipt — "acknowledge" is the candidate/TA confirming
// something already happened (contact was received, interview slot is
// confirmed), not the TA actively advancing the pipeline the way Shortlist/
// Forward to CSE/Complete Interview Round do. Deliberately kept out of
// EMERALD_ACTIONS — emerald renders as a bold solid-fill CTA (see
// src/lib/statusStyles.js's TONE_BUTTON_CLASSES.emerald.solid), and giving
// a routine confirmation that same visual weight as a real milestone
// overstates it. Teal reads as a calmer "confirmed" signal, distinct from
// blue's "we reached out" — and was otherwise completely unused here.
const TEAL_ACTIONS = ['acknowledge_contact', 'acknowledge_interview'];

// Scheduling / time-bound actions.
const VIOLET_ACTIONS = ['assign_slot', 'reschedule_slot'];

// Profile / data capture.
const INDIGO_ACTIONS = ['interview_profile_update'];

// Neutral utility (default flow).
const SLATE_ACTIONS = ['refresh_candidate_session'];

const ACTION_SCHEME = Object.fromEntries([
  ...RED_ACTIONS.map((key) => [key, 'red']),
  ...EMERALD_ACTIONS.map((key) => [key, 'emerald']),
  ...BLUE_ACTIONS.map((key) => [key, 'blue']),
  ...TEAL_ACTIONS.map((key) => [key, 'teal']),
  ...VIOLET_ACTIONS.map((key) => [key, 'violet']),
  ...INDIGO_ACTIONS.map((key) => [key, 'indigo']),
  ...SLATE_ACTIONS.map((key) => [key, 'slate']),
]);

/** Resolve an actionKey to its color scheme name, falling back to 'gray'. */
export const getActionScheme = (actionKey) =>
  ACTION_SCHEME[actionKey] || 'gray';
